// The hidden user message that turns an approved plan into prose.
//
// A chat started from the selection bubble is about replacing one highlighted passage. The server
// only engages its in-place replacement mode — with the length budget that keeps a paragraph a
// paragraph — when the message it is answering carries the <SELECTED_TEXT> tag, and it reads the
// "make it longer" wish off that same message. The execute turn used to send neither, so a plan
// for a 70-word paragraph came back as a 600-word scene. It now re-carries both.

const PLAN_IS_BINDING =
  'Treat the plan as binding: include every beat in its stated order without replacing or contradicting it, and follow any stated guidelines or constraints (tone, point of view, length target, setting, style notes) throughout the prose. If the plan contains quoted dialogue, use that wording in the scene and expand naturally around it. If it describes a conversation without exact lines, dramatize and expand that exchange while preserving its intent, information, and emotional turn.'

export interface PlanSelection {
  text: string
  instructions: string
}

export function buildExecutePlanPrompt(
  planContent: string,
  selection: PlanSelection | null | undefined
): string {
  const planBlock = `<APPROVED_PLAN>\n${planContent}\n</APPROVED_PLAN>`
  if (!selection || !selection.text.trim()) {
    return `Write this scene from the approved plan below. ${PLAN_IS_BINDING}\n\n${planBlock}`
  }
  const originalRequest = selection.instructions.trim()
  return `I have highlighted the following text in my story:\n<SELECTED_TEXT>\n${selection.text}\n</SELECTED_TEXT>\n\nMy instructions:\nReplace this passage with prose written from the approved plan below. The result replaces exactly this passage, in place, at the length the plan's guidelines call for — do not carry the story past where the passage ends.${
    originalRequest ? ` My original request for this passage: ${originalRequest}` : ''
  } ${PLAN_IS_BINDING}\n\n${planBlock}`
}
